import { action, computed, flow, makeObservable, observable } from "mobx";
import api from "../utils/apiService";
import { makePersistable } from 'mobx-persist-store';
import isEqual from 'lodash/isEqual';

import { apiFetcher } from "../utils/fetch";
import { updateItems } from "./utils";

const defaultPagination = {
  page: 1,
  pageSize: 50,
  totalPages: 1,
  totalData: 0,
  hasNextPage: false,
  hasPrevPage: false
}
const defaultStatus = ["inprogress", "end_soon", "booked", "-"];
const defaultFilter = { search: "", status: defaultStatus, frequency: [], propertyId: "", pagination: defaultPagination }
export default class Tenant {
  constructor() {
    this.selected = {};
    this.filters = defaultFilter;
    this.items = [];
    this.itemWaitingList = 0;

    makeObservable(this, {
      selected: observable,
      filters: observable,
      items: observable,
      itemWaitingList: observable,
      filteredItems: computed,
      setSelected: action,
      setFilters: action,
      setResetFilter: action,
      setWaitingList: action,
      fetch: flow,
      fetchOne: flow,
      fetchPaginate: flow,
      create: flow,
      update: flow,
      delete: flow,
      deleteAll: flow,
      deleteOccwithRent: flow,
      deleteAll: flow,
      sendEmail: flow,
      sendMessage: flow,
      sendPaymentByEmail: flow,
      sendPaymentByWhatsapp: flow,
      sendRentalAgreementByWhatsapp: flow,
      downloadRentalAgreementByWhatsapp: flow,
      sendTemplateRulesByWhatsapp: flow,
      changeStepperMode: flow,
      changeStepper: flow,
      endLease: flow,
      extendRent: flow,
      changeRent: flow,
      editProfilePicture: flow
    });
    makePersistable(this, {
      name: 'Tenant', properties: ['selected', 'items', 'itemWaitingList', 'filters'],
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      // expireIn: 604800000, // 7 day in milliseconds to match refresh token expiry time
      // removeOnExpiration: true,
    });

  }

  get filteredItems() {
    const { search, status, frequency, propertyId } = this.filters || {};
    // simple in-memory filtering to reflect filters on already-loaded items
    return this.items.filter(item => {
      if (status?.length && !status.includes(item.status)) return false;
      if (frequency?.length && !frequency.includes(item.frequency)) return false;
      if (propertyId && item.property?._id !== propertyId) return false;
      if (search) {
        const text = search.toLowerCase();
        if (!(`${item.name}`.toLowerCase().includes(text) ||
          `${item.contacts?.[0]?.phone || ''}`.includes(text) ||
          `${item.room?.name || ''}`.toLowerCase().includes(text))) {
          return false;
        }
      }
      return true;
    });
  }

  setSelected = (tenant) => (this.selected = tenant);
  setSelectedRoom = (room) => (this.selected.room = room);
  setFilters = (
    { search = "", status = [], frequency = [], propertyId = "", pagination = defaultPagination } = {},
    { fetch = true } = {}
  ) => {
    const newFilters = { search, status, frequency, propertyId, pagination };

    // Avoid refetch if filters unchanged
    if (isEqual(newFilters, this.filters)) {
      return;
    }

    // assign new filters
    this.filters = newFilters;

    // trigger fetch (non-blocking). callers may pass { fetch: false } to skip.
    if (fetch) {
      // fetchPaginate is a generator (flow) — calling it starts the async action
      this.fetchPaginate();
    }
  };

  setResetFilter = () => {
    this.setFilters(defaultFilter);
  };

  setWaitingList = (waitingListCount) => {
    this.itemWaitingList = waitingListCount;
  };

  *fetch(query, { setItem = true } = {}) {
    try {
      let baseURL = '/tenants';
      let queryString;
      if (query) {
        queryString = Object.keys(query)
          .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
          .join("&");
      }
      const fullURL = `${baseURL}?${queryString}`;
      const response = yield apiFetcher().get(fullURL);

      if (setItem) {
        this.items = response.data;
        if (this.selected._id) {
          this.setSelected(this.items.find((item) => item._id === this.selected._id) || {});
        }
      }
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchOne(tenantId) {
    try {
      const response = yield apiFetcher().get(`/tenants/${tenantId}`);
      const updatedTenant = response.data;
      this.items = updateItems(updatedTenant, this.items);
      if (this.selected?._id === updatedTenant._id) {
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchPaginate() {
    try {
      const baseURL = `/tenants/with-paginate`;
      // extract filters
      const { search, status, frequency, propertyId, pagination } = this.filters;

      // build query and fetch data from API
      const filtersApplied = {
        page: pagination.page,
        limit: defaultPagination.pageSize,
      };
      if (search) {
        filtersApplied.search = search;
      }
      if (propertyId) {
        filtersApplied.property = propertyId;
      }

      // Create a URLSearchParams object from the JavaScript object
      const params = new URLSearchParams(filtersApplied);
      status.forEach((stat) => {
        params.append('status', stat);
      });
      frequency.forEach((freq) => {
        params.append('frequency', freq);
      })

      // Convert the URLSearchParams object to a query string
      const queryString = params.toString();

      const fullURL = `${baseURL}${queryString ? `?${queryString}` : ""}`;
      const response = yield apiFetcher().get(fullURL);

      this.items = response.data.data;
      this.filters.pagination = {
        page: response.data.pagination.page,
        pageSize: response.data.pagination.limit,
        totalPages: response.data.pagination.pages,
        totalData: response.data.pagination.total,
        hasNextPage: response.data.pagination.hasNextPage,
        hasPrevPage: response.data.pagination.hasPrevPage,
      };

      this.setWaitingList(response.data.stats.waitingList);

      // this.items = response.data;
      if (this.selected._id) {
        this.setSelected(this.items.find((item) => item._id === this.selected._id) || {});
      }

      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *create(tenant) {
    try {
      const response = yield apiFetcher().post("/tenants", tenant);
      const createdTenant = response.data;
      this.items = updateItems(createdTenant, this.items);

      return { status: 200, data: createdTenant };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *update(tenant) {
    try {
      const response = yield apiFetcher().patch(`/tenants/${tenant._id}`, tenant);
      const updatedTenant = response.data;
      this.items = updateItems(updatedTenant, this.items);
      if (this.selected?._id === updatedTenant._id) {
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      const status = error?.response?.status;
      const errors = error?.response?.data?.errors || [];
      return { status, errors };
    }
  }

  *delete(ids) {
    try {
      yield apiFetcher().delete(`/tenants/${ids.join(",")}`);
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deleteOccwithRent(id) {
    try {
      const response = yield apiFetcher().delete(`/tenants/rent/${id}`);
      const successMessage = response?.data || "Penyewa berhasil dihapus";
      return { status: 200, message: successMessage };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deleteAll() {
    try {
      const response = yield apiFetcher().delete(`/tenants`);
      const successMessage = response?.data || "Semua data penyewa berhasil dihapus";
      return { status: 200, message: successMessage };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *deleteAll() {
    try {
      const response = yield apiFetcher().delete(`/tenants`);
      const successMessage = response?.data || "Semua data penyewa berhasil dihapus";
      return { status: 200, message: successMessage };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *sendEmail(id, payload) {
    try {
      const response = yield apiFetcher().post(`/emails/invoice/${id}`, payload);
      return { status: 200, data: response.data };
      // yield apiFetcher().post(`/emails/invoice/${id}`, payload);
      // return 200;
    } catch (error) {
      return error.response.status;
    }
  }

  *sendMessage(id, payload) {
    try {
      // yield apiFetcher().post(`/messages/invoice/${id}`, payload);
      // return 200;
      const response = yield apiFetcher().post(`/messages/invoice/${id}`, payload);
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *sendPaymentByEmail(id, payload) {
    try {
      const response = yield apiFetcher().post(`/emails/invoice/${id}/paymentreceipt`, payload);
      return { status: 200, data: response.data };
    } catch (error) {
      return error.response.status;
    }
  }

  *sendPaymentByWhatsapp(id, payload) {
    try {
      const response = yield apiFetcher().post(`/messages/invoice/${id}/paymentreceipt`, payload);
      return { status: 200, data: response.data };
    } catch (error) {
      return error.response.status;
    }
  }

  *sendRentalAgreementByWhatsapp(id, payload) {
    try {
      const response = yield apiFetcher().post(
        `/rentagreement/sendtenant/rentAgreement/${id}`,
        payload
      );
      const responseTenant = response.data;
      if (this.selected?._id === responseTenant.occupantId) {
        this.setSelectedRoom(responseTenant.roomRentAgreement);
      }
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error.response.status, message: error.response.data.message };
    }
  }

  *sendTemplateRulesByWhatsapp(id, payload) {
    try {
      const response = yield apiFetcher().post(`/messages/rules/${id}`, payload);
      return { status: 200, data: response.data }
    } catch (error) {
      return { status: error.response.status, message: error.response.data.error.message };
    }
  }

  *downloadRentalAgreementByWhatsapp(id, payload) {
    try {
      const response = yield apiFetcher().post(
        `/rentagreement/download/${id}`
      );
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error.response.status, message: error.response.data.error.message };
    }
  }

  *changeStepperMode(stepperMode, updateSelected = true) {
    try {
      let selectedTenant = {
        ...this.selected,
        stepperMode: stepperMode,
      };

      const response = yield apiFetcher().patch(`/tenants/${selectedTenant._id}`, selectedTenant);
      const updatedTenant = response.data;

      if (updateSelected && this.selected._id === updatedTenant._id) {
        this.items = updateItems(updatedTenant, this.items);
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  // TODO: use new change-step endpoint to update tenant stepperMode. SKOS-175
  *changeStepper(payload) {
    try {
      const response = yield apiFetcher().post(`/tenants/${payload.identifier}/change-step`, payload);
      const updatedTenant = response.data;
      // if (this.selected?._id === updatedTenant._id) {
      //   this.items = updateItems(updatedTenant, this.items);
      //   this.setSelected(updatedTenant);
      // }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  /**
   * Ends the lease for a specified tenant.
   *
   * @async
   * @generator
   * @param {string} tenantId - The ID of the tenant whose lease is to be ended.
   * @param {Object} data - The data to be sent in the request body for ending the lease.
   *   {
   *      endDate: string,
   *      isRemoveInvoice: boolean,
   *      checkoutNote: string
   *   }
   * @yields {Promise<Object>} The API response containing the updated tenant data.
   * @returns {Object} An object containing the status code and the updated tenant data (if successful).
   */
  *endLease(tenantId, data) {
    try {
      const response = yield apiFetcher().post(`/tenants/${tenantId}/end-lease`, data);
      const updatedTenant = response.data;
      this.items = updateItems(updatedTenant, this.items);
      if (this.selected._id === updatedTenant._id) {
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  /**
   * Extend/Prolong tenant rent
   * @param {String} tenantId
   * @param {Object} payload
   * {
   *  endDate: String // use format 'DD/MM/YYYY'
   *  roomId: String  // id of selected tenant room
   * }
   * @returns {Object} An object containing the status code and the updated tenant data (if successful).
   */
  *extendRent(tenantId, payload) {
    try {
      const response = yield apiFetcher().patch(`/tenants/${tenantId}/extend-rent`, payload);
      const updatedTenant = response.data;

      this.items = updateItems(updatedTenant, this.items);
      if (this.selected?._id === updatedTenant._id) {
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      const status = error?.response?.status;
      const errors = error?.response?.data?.errors || [];
      return { status, errors };
    }
  }

  /**
   * Change tenant rent
   * @param {String} tenantId
   * @param {Object} payload
   * {
   *
   * }
   * @returns {Object} An object containing the status code and the updated tenant data (if successful).
   */
  *changeRent(tenantId, payload) {
    try {
      const response = yield apiFetcher().patch(`/tenants/${tenantId}/change-rent`, payload);
      const updatedTenant = response.data;

      this.items = updateItems(updatedTenant, this.items);
      if (this.selected?._id === updatedTenant._id) {
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      const status = error?.response?.status;
      const errors = error?.response?.data?.errors || [];
      return { status, errors };
    }
  }

  *editProfilePicture(tenantId, payload) {
    try {
      const response = yield apiFetcher().patch(`/tenants/${tenantId}/profile-picture`, payload);
      const updatedTenant = response.data;

      this.items = updateItems(updatedTenant, this.items);
      if (this.selected?._id === updatedTenant._id) {
        this.setSelected(updatedTenant);
      }
      return { status: 200, data: updatedTenant };
    } catch (error) {
      const status = error?.response?.status;
      const errors = error?.response?.data?.errors || [];
      return { status, errors };
    }
  }
}
